import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import useAsyncEffect from 'use-async-effect';
import api from '../../api/api';
import { to } from '../../util/util';
import { history } from '../../index';
import { RegisterListingResult } from '../../model/RegisterListingResult';
import VirtualizedStatsTable from '../campaignStats/VirtualizedStatsTable';

interface IProps {
    ticker: string;
    exchange: string;
}

const Wrapper = styled.div`
    .register-listing__filter {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin: 0.75rem 0;
    }
    .register-listing__filter input {
        width: 320px;
    }
    .register-listing__count {
        color: #666;
        font-size: 0.8rem;
    }
    .register-listing__name {
        color: #0b5cad;
        cursor: pointer;
    }
    .register-listing__member {
        padding-left: 1.5rem;
        color: #555;
    }
`;

const RegisterListing: React.FC<IProps> = (props: IProps) => {

    const [result, setResult] = useState<RegisterListingResult | null>(null);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');

    useAsyncEffect(async (isMounted: () => boolean) => {
        const [err, data] = await to(api().getRegisterListing(props.ticker, props.exchange));
        if (!isMounted()) return;
        if (err || !data.success) setError(err?.message || data?.message || 'Failed to fetch register listing');
        else setResult(data);
    }, []);

    // only the primary listed security — options etc. are visible on the holder details page
    const primaryIndex = useMemo(() => Math.max((result?.securityCodes || []).indexOf(props.ticker), 0), [result, props.ticker]);

    // each row: [rank, name, email, holderNumber, holding, %issuedCapital, model];
    // rank is fixed by holding (largest = 1) so it survives re-sorting by other columns.
    // holders sharing a holderGroup collapse into one aggregate row (not clickable) whose
    // members ride along as childRows — mirroring the Top Shareholders grouping.
    const data = useMemo<any[][]>(() => {
        const holders = (result?.holders || []).map(h => ({ h, holding: h.balances?.[primaryIndex] ?? 0 }));
        const total = holders.reduce((s, x) => s + x.holding, 0);

        const groups = new Map<string, typeof holders>();
        const singles: typeof holders = [];
        holders.forEach(x => {
            if (!x.h.holderGroup) { singles.push(x); return; }
            groups.set(x.h.holderGroup, [...(groups.get(x.h.holderGroup) || []), x]);
        });

        const entries = [
            ...singles.map(x => ({ name: x.h.name, email: x.h.email, holderNumber: x.h.holderNumber, holding: x.holding, holder: x.h, members: undefined as typeof holders | undefined })),
            ...Array.from(groups.entries()).map(([name, members]) => ({
                name, email: '', holderNumber: '',
                holding: members.reduce((s, m) => s + m.holding, 0),
                holder: undefined, members: members.sort((a, b) => b.holding - a.holding),
            })),
        ].sort((a, b) => b.holding - a.holding);

        return entries.map((e, i) => [i + 1, e.name, e.email, e.holderNumber, e.holding, total ? e.holding * 100 / total : 0, {
            holder: e.holder,
            childRows: e.members?.map(m => [null, m.h.name, m.h.email, m.h.holderNumber, m.holding, null, { holder: m.h, isMember: true }]),
        }]);
    }, [result, primaryIndex]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const matches = (r: any[]) => [r[1], r[2], r[3]].some(c => String(c ?? '').toLowerCase().includes(q));
        return q
            ? data.filter(r => matches(r) || (r[6].childRows || []).some(matches))
            : data;
    }, [data, query]);

    if (error) return <Wrapper><p style={{ color: '#c0392b' }}>{error}</p></Wrapper>;
    if (!result) return <Wrapper><p>Loading register listing&hellip; (first load can take a minute while reports generate)</p></Wrapper>;

    const headers = ['#', 'Name', 'Email', 'Holder No.', 'Holding', '% Issued Capital'];

    const openHolder = (holderId: number) => {
        const base = window.location.pathname.split('/').slice(0, 4).join('/');
        history.push(`${base}/Search?holderId=${holderId}`);
    };

    const cellFormatter = (columnName: string, cellData: any, model: any) => {
        if (cellData === null) return <></>; // member rows leave rank and % blank
        if (columnName === 'Name') {
            if (model.isMember)
                return <span className="register-listing__name register-listing__member" onClick={() => openHolder(model.holder.holderId)}>&#8627; {cellData}</span>;
            if (!model.holder) // group row — the group itself is not a holder, so not clickable
                return <b>{cellData}</b>;
            return <span className="register-listing__name" onClick={() => openHolder(model.holder.holderId)}>{cellData}</span>;
        }
        if (columnName === 'Holding')
            return <>{Number(cellData).toLocaleString()}</>;
        if (columnName === '% Issued Capital')
            return <>{Number(cellData).toFixed(2)}%</>;
        return null;
    };

    return <Wrapper>
        <div className="register-listing__filter">
            <input type="text" placeholder="Filter by name, holder no. or email..."
                value={query} onChange={e => setQuery(e.target.value)} />
            <span className="register-listing__count">Showing {filtered.length.toLocaleString()} of {data.length.toLocaleString()} holders</span>
        </div>
        <VirtualizedStatsTable
            headers={headers}
            data={filtered}
            cellFormatter={cellFormatter}
            sortIndex={0}
            sortDirection="asc"
            gridColumns="50px minmax(200px, 1.6fr) minmax(180px, 1.4fr) 110px 110px 130px"
        />
    </Wrapper>;
}

export default RegisterListing;
